import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { 
  Bell, 
  Send, 
  MessageCircle, 
  CheckCircle2, 
  ExternalLink,
  ListTodo,
  FolderKanban,
  CreditCard,
  Clock,
  MessageSquare,
  Settings,
  RefreshCw,
  AlertCircle,
  Mail,
  CalendarDays,
} from "lucide-react";

const CATEGORY_ICONS = {
  tasks: ListTodo,
  projects: FolderKanban,
  payments: CreditCard,
  requests: MessageSquare,
  deadlines: Clock,
  absences: CalendarDays,
};

const CATEGORY_COLORS = {
  tasks: "text-sky-400",
  projects: "text-purple-400",
  payments: "text-green-400",
  requests: "text-orange-400",
  deadlines: "text-red-400",
  absences: "text-amber-400",
};

const NotificationSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [settings, setSettings] = useState({
    telegram_enabled: false,
    telegram_chat_id: "",
    telegram_username: "",
    notifications: {},
    email_enabled: true,
    email_notifications: {},
  });
  
  const [notificationTypes, setNotificationTypes] = useState({});
  const [botInfo, setBotInfo] = useState(null);
  const [showBotDialog, setShowBotDialog] = useState(false);
  const [chatIdInput, setChatIdInput] = useState("");

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, botRes] = await Promise.all([
        api.get("/notification-settings"),
        api.get("/notification-settings/bot-info"),
      ]);

      const s = settingsRes.data.settings || {};
      setSettings({
        telegram_enabled: s.telegram_enabled || false,
        telegram_chat_id: s.telegram_chat_id || "",
        telegram_username: s.telegram_username || "",
        notifications: s.notifications || {},
        email_enabled: s.email_enabled !== false,
        email_notifications: s.email_notifications || {},
      });
      setNotificationTypes(settingsRes.data.notification_types || {});
      setBotInfo(botRes.data);
      setChatIdInput(s.telegram_chat_id || "");
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      await api.put("/notification-settings", settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testTelegram = async () => {
    try {
      setTesting(true);
      await api.post("/notification-settings/test-telegram");
      toast.success("Test message sent! Check your Telegram.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test message");
    } finally {
      setTesting(false);
    }
  };

  const connectTelegram = async () => {
    if (!chatIdInput) {
      toast.error("Please enter your Chat ID");
      return;
    }
    const newSettings = {
      ...settings,
      telegram_chat_id: chatIdInput,
      telegram_enabled: true
    };
    setSettings(newSettings);
    try {
      setSaving(true);
      await api.put("/notification-settings", newSettings);
      await api.post("/notification-settings/test-telegram");
      toast.success("Telegram connected!");
      setShowBotDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Telegram");
    } finally {
      setSaving(false);
    }
  };

  const toggleTgNotification = (key) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: !prev.notifications[key] }
    }));
  };

  const toggleEmailNotification = (key) => {
    setSettings(prev => ({
      ...prev,
      email_notifications: { ...prev.email_notifications, [key]: !prev.email_notifications[key] }
    }));
  };

  const toggleAllTgInCategory = (category, enable) => {
    const keys = Object.entries(notificationTypes)
      .filter(([_, c]) => c.category === category)
      .map(([k]) => k);
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, ...Object.fromEntries(keys.map(k => [k, enable])) }
    }));
  };

  const toggleAllEmailInCategory = (category, enable) => {
    const keys = Object.entries(notificationTypes)
      .filter(([_, c]) => c.category === category)
      .map(([k]) => k);
    setSettings(prev => ({
      ...prev,
      email_notifications: { ...prev.email_notifications, ...Object.fromEntries(keys.map(k => [k, enable])) }
    }));
  };

  const groupedNotifications = Object.entries(notificationTypes).reduce((acc, [key, config]) => {
    const cat = config.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ key, ...config });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const NotificationTypesList = ({ settingsKey, toggleFn, toggleAllFn }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {Object.entries(groupedNotifications).map(([category, items]) => {
        const Icon = CATEGORY_ICONS[category] || Bell;
        const colorClass = CATEGORY_COLORS[category] || "text-zinc-400";
        const allEnabled = items.every(item => settings[settingsKey][item.key]);
        return (
          <Card key={category} className="bg-zinc-800/30 border-zinc-700/50">
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300 flex items-center gap-1.5 capitalize">
                  <Icon className={cn("h-3.5 w-3.5", colorClass)} />
                  {category}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleAllFn(category, !allEnabled)}
                  className="h-6 text-[11px] text-zinc-500 hover:text-zinc-50 px-2"
                >
                  {allEnabled ? "Disable All" : "Enable All"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-3 space-y-1">
              {items.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between py-1.5 rounded hover:bg-zinc-800/50 px-1.5 -mx-1.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-300">{item.name}</p>
                    <p className="text-[11px] text-zinc-600 truncate">{item.description}</p>
                  </div>
                  <Switch
                    checked={settings[settingsKey][item.key] !== false}
                    onCheckedChange={() => toggleFn(item.key)}
                    className="scale-75"
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-5 max-w-5xl" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-500" />
            Notification Settings
          </h1>
          <p className="text-xs text-zinc-500">Configure how and when you receive notifications</p>
        </div>
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 h-9"
          data-testid="save-settings-btn"
        >
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </div>

      {/* Email Notifications */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <Mail className="h-4 w-4 text-amber-400" />
              Email Notifications
            </CardTitle>
            <Switch
              checked={settings.email_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, email_enabled: checked }))}
              data-testid="email-master-toggle"
            />
          </div>
          <CardDescription className="text-xs">Receive notifications via email</CardDescription>
        </CardHeader>
        {settings.email_enabled && (
          <CardContent className="pt-0">
            <NotificationTypesList
              settingsKey="email_notifications"
              toggleFn={toggleEmailNotification}
              toggleAllFn={toggleAllEmailInCategory}
            />
          </CardContent>
        )}
      </Card>

      {/* Telegram Notifications */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <Send className="h-4 w-4 text-sky-400" />
            Telegram Integration
          </CardTitle>
          <CardDescription className="text-xs">Receive instant notifications via Telegram</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center",
                settings.telegram_enabled && settings.telegram_chat_id ? "bg-green-500/20" : "bg-zinc-700"
              )}>
                <MessageCircle className={cn(
                  "h-4 w-4",
                  settings.telegram_enabled && settings.telegram_chat_id ? "text-green-400" : "text-zinc-400"
                )} />
              </div>
              <div>
                <p className="text-sm text-zinc-50 font-medium">
                  {settings.telegram_enabled && settings.telegram_chat_id ? "Connected" : "Not Connected"}
                </p>
                {settings.telegram_chat_id && (
                  <p className="text-[11px] text-zinc-500 font-mono">ID: {settings.telegram_chat_id}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {settings.telegram_chat_id && (
                <Button variant="outline" size="sm" onClick={testTelegram} disabled={testing} className="border-zinc-700 text-zinc-300 h-8 text-xs">
                  {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" />Test</>}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setShowBotDialog(true)}
                className={cn("h-8 text-xs", settings.telegram_chat_id ? "bg-zinc-700 hover:bg-zinc-600" : "bg-sky-600 hover:bg-sky-700")}
              >
                <Settings className="h-3.5 w-3.5 mr-1" />
                {settings.telegram_chat_id ? "Reconfigure" : "Connect"}
              </Button>
            </div>
          </div>

          {settings.telegram_chat_id && (
            <div className="flex items-center justify-between p-2.5 bg-zinc-800/30 rounded-lg">
              <div>
                <p className="text-sm text-zinc-300">Enable Telegram Notifications</p>
                <p className="text-[11px] text-zinc-500">Master toggle</p>
              </div>
              <Switch
                checked={settings.telegram_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, telegram_enabled: checked }))}
                data-testid="telegram-master-toggle"
              />
            </div>
          )}

          {settings.telegram_enabled && settings.telegram_chat_id && (
            <NotificationTypesList
              settingsKey="notifications"
              toggleFn={toggleTgNotification}
              toggleAllFn={toggleAllTgInCategory}
            />
          )}

          {!settings.telegram_chat_id && (
            <div className="flex items-start gap-3 p-3 bg-blue-500/5 rounded-lg border border-blue-500/10">
              <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-zinc-400">
                Connect Telegram to manage per-type notifications. Click Connect above.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect Bot Dialog */}
      <Dialog open={showBotDialog} onOpenChange={setShowBotDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Send className="h-5 w-5 text-sky-400" />
              Connect Telegram Bot
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {botInfo && (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-400 flex items-center gap-2">
                    <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Step 1</Badge>
                    Open the Bot
                  </Label>
                  <Button
                    variant="outline"
                    className="w-full border-zinc-700 hover:bg-zinc-800 justify-between"
                    onClick={() => window.open(botInfo.bot_link, '_blank')}
                  >
                    <span className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-sky-400" />
                      @{botInfo.bot_username}
                    </span>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 flex items-center gap-2">
                    <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Step 2</Badge>
                    Start the Bot & get Chat ID
                  </Label>
                  <p className="text-sm text-zinc-500">
                    Press <code className="bg-zinc-800 px-1 py-0.5 rounded">Start</code> or send <code className="bg-zinc-800 px-1 py-0.5 rounded">/start</code>. The bot will reply with your Chat ID.
                  </p>
                </div>
                <div className="space-y-2 pt-2 border-t border-zinc-800">
                  <Label className="text-zinc-400">Your Chat ID</Label>
                  <Input
                    value={chatIdInput}
                    onChange={(e) => setChatIdInput(e.target.value)}
                    placeholder="e.g., 123456789"
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBotDialog(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={connectTelegram} disabled={saving || !chatIdInput} className="bg-sky-600 hover:bg-sky-700">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Connect & Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettingsPage;
